import React, { useEffect, useState } from 'react';
import { Card, Form, Input, Select, Switch, Button, message, Divider } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import api from '@/services/api';
import { useTheme } from '@/contexts/ThemeContext';

const Settings = () => {
  const { isDark } = useTheme();
  const [form] = Form.useForm();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, categoriesRes] = await Promise.all([
        api.get('/settings'),
        api.get('/categories'),
      ]);
      form.setFieldsValue(settingsRes.data.data);
      setCategories(categoriesRes.data.data);
    } catch (error) {
      message.error('Failed to fetch settings');
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      await api.put('/settings', values);
      message.success('Settings saved successfully');
    } catch (error) {
      message.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700, fontFamily: 'IBM Plex Sans, sans-serif', letterSpacing: '-0.02em', marginBottom: 8 }}>
          Website Settings
        </h1>
        <p style={{ fontSize: 14, color: isDark ? '#A1A1AA' : '#71717A', margin: 0 }}>
          Configure your website settings and preferences
        </p>
      </div>

      <Card
        style={{
          borderRadius: 0,
          border: `1px solid ${isDark ? '#27272A' : '#E4E4E7'}`,
          background: isDark ? '#18181B' : '#FFFFFF',
          maxWidth: 800,
        }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <div style={{ marginBottom: 24 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, fontFamily: 'IBM Plex Sans, sans-serif', marginBottom: 16 }}>
              General Settings
            </h3>
            <Form.Item
              name="siteName"
              label="Site Name"
              rules={[{ required: true, message: 'Please enter site name' }]}
            >
              <Input style={{ borderRadius: 0 }} data-testid="site-name-input" />
            </Form.Item>
            <Form.Item
              name="logoUrl"
              label="Logo URL"
            >
              <Input style={{ borderRadius: 0 }} placeholder="https://example.com/logo.png" data-testid="logo-url-input" />
            </Form.Item>
          </div>

          <Divider style={{ margin: '32px 0' }} />

          <div style={{ marginBottom: 24 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, fontFamily: 'IBM Plex Sans, sans-serif', marginBottom: 16 }}>
              Homepage Settings
            </h3>
            <Form.Item
              name="featuredCategoryId"
              label="Featured Category"
            >
              <Select style={{ borderRadius: 0 }} data-testid="featured-category-select">
                {categories.map((category) => (
                  <Select.Option key={category.id} value={category.id}>
                    {category.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          <Divider style={{ margin: '32px 0' }} />

          <div style={{ marginBottom: 24 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, fontFamily: 'IBM Plex Sans, sans-serif', marginBottom: 16 }}>
              System Settings
            </h3>
            <Form.Item
              name="maintenanceMode"
              label="Maintenance Mode"
              valuePropName="checked"
            >
              <Switch style={{ borderRadius: 0 }} data-testid="maintenance-mode-switch" />
            </Form.Item>
            <Form.Item
              name="theme"
              label="Default Theme"
            >
              <Select style={{ borderRadius: 0 }} data-testid="default-theme-select">
                <Select.Option value="light">Light</Select.Option>
                <Select.Option value="dark">Dark</Select.Option>
              </Select>
            </Form.Item>
          </div>

          <Divider style={{ margin: '32px 0' }} />

          <div style={{ marginBottom: 24 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, fontFamily: 'IBM Plex Sans, sans-serif', marginBottom: 16 }}>
              Advertising Settings
            </h3>
            <Form.Item
              name="globalAdsEnabled"
              label="Global Ads Master Toggle"
              valuePropName="checked"
            >
              <Switch style={{ borderRadius: 0 }} data-testid="global-ads-switch" />
            </Form.Item>
          </div>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              loading={loading}
              style={{ borderRadius: 0, fontWeight: 500, height: 40 }}
              data-testid="save-settings-button"
            >
              Save Settings
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default Settings;